// TemplateDisplayStyles.ts
import {styled} from '@mui/material/styles';
// TemplateDisplay.tsx
import React, {forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useMobile} from '../../../context/MobileContext';
import {Design} from "../../../models/Template";
import {SceneRenderer} from "../../../scene/SceneRenderer";
import {SceneValues} from "../../../scene/types";
import {registerSceneFonts} from "../../../scene/fonts";
import {useMergedScene, useTemplate} from "../../../context/TemplateContext";
import {isUserLayerId} from "../../../scene/sceneToKonva";
import {moveRow} from "../../../scene/rowReposition";

export const TemplateWrapper = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    // Full-width measurable box so the contain-fit below can clamp wide
    // designs to the card instead of overflowing it.
    width: '100%',
    minWidth: 0,
});

export const TemplateContainer = styled('div')({
    position: 'relative',
});

export const TemplateContent = styled('div')({
    position: 'absolute',
    top: 0,
    right: 0,
    transformOrigin: 'top right',
});

interface TemplateDisplayProps {
    design: Design;
    containerHeight: number;
    mobilePadding?: number;
    onTemplateLoad?: () => void;
    /** Current input values (scene engine); legacy 'html' applies them to the DOM elsewhere. */
    values?: SceneValues;
    /** Template fonts (scene engine), registered as @font-face for Konva. */
    fonts?: Record<string, { urls?: string[] | null } | null>;
    /** Editor interactions (select/drag/reposition/text-edit). False = read-only
     *  preview, e.g. the admin import-preview screen. Defaults true. */
    editable?: boolean;
    /** Fired when an editable IMAGE element on the scene canvas is tapped —
     *  opens the asset selection gallery. Wired in TemplateEditor. Without
     *  this, image clicks fall through to the default selection behaviour. */
    onImageInputClick?: (inputKey: string) => void;
    /** Fired when an editable TEXT element on the scene canvas is tapped.
     *  Supplying it replaces the on-canvas inline editor for that click — Fill
     *  Mode uses it to raise its top text sheet. */
    onTextInputClick?: (inputKey: string) => void;
    /** Show the scene's dashed editable-text hints (scene engine, default true).
     *  Fill Mode turns them off — its marker overlay already flags each field. */
    sceneTextHints?: boolean;
    /** Index of `design` within template.designs. Required for USER LAYERS: the
     *  layer store is keyed by `design.key || String(index)`, so a surface that
     *  omits this (the admin previews) simply renders the template scene. */
    designIndex?: number;
}

const TemplateDisplay = forwardRef<HTMLDivElement, TemplateDisplayProps>(({
                                                                              design,
                                                                              containerHeight,
                                                                              onTemplateLoad,
                                                                              values,
                                                                              fonts,
                                                                              editable = true,
                                                                              onImageInputClick,
                                                                              onTextInputClick,
                                                                              mobilePadding = 45,
                                                                              sceneTextHints = true,
                                                                              designIndex,
                                                                          }, ref) => {
    const {isMobile} = useMobile();
    const {sceneTransforms, setSceneTransforms, selectedSceneId, setSelectedSceneId, setSelections, template,
        selectedTransformedImage, setSelectedTransformedImage, imageFit, repositioningRow,
        setRepositioningRow} = useTemplate();
    // The scene the canvas actually draws: template elements + this design's
    // user layers, minus hidden rows / switched-off layers. Stable identity
    // while nothing changes (SceneRenderer resets its load accounting on a new
    // scene object).
    const mergedScene = useMergedScene(design, designIndex);
    // Row reposition: the members the canvas DRAWS move live; the commit
    // (onGroupMove) writes every member of the FULL template scene, so a layer
    // switched off inside the row travels with it (see scene/rowReposition).
    const renderedScene = mergedScene ?? design?.scene;
    const groupIds = useMemo(
        () => (repositioningRow && renderedScene
            ? renderedScene.elements.filter((el) => el.groupId === repositioningRow).map((el) => el.id)
            : null),
        [repositioningRow, renderedScene],
    );
    const divRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    // Available width of the wrapper (the card's inner width). 0 until first
    // measure — the fit math falls back to height-only sizing until then.
    const [availableWidth, setAvailableWidth] = useState(0);

    // Measure before paint (no oversized flash) and track card/window resizes.
    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const updateWidth = () => setAvailableWidth(wrapper.getBoundingClientRect().width);
        updateWidth();
        // Observe the WRAPPER, not the fitted container — observing the child
        // we size from this measurement would create a feedback loop.
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(wrapper);
        return () => resizeObserver.disconnect();
    }, []);

    // Deselect the canvas AND end any reposition (resets the floating bar from
    // Reset/Apply back to the "Reposition" button). Used by Escape, tap-outside,
    // and clicking a non-editable element.
    const endSceneSelection = () => {
        setSelectedSceneId(null);
        setSelectedTransformedImage(undefined);
        setRepositioningRow(null);
    };
    let observer: MutationObserver;

    // Register scene fonts as @font-face (sanitized family) so Konva renders the
    // real fonts instead of the dotted-name fallback.
    useEffect(() => {
        if (design?.scene && fonts) registerSceneFonts(fonts);
    }, [design, fonts]);

    useEffect(() => {
        if (divRef.current) {
            observer = new MutationObserver(handleMutation);
            observer.observe(divRef.current, {
                childList: true,
                attributes: true,
                characterData: true
            });

            // first time mutation is not sent
            onTemplateLoad?.();
        }

        return () => {
            observer?.disconnect();
        };
    }, []);

    const handleMutation = () => {
        onTemplateLoad?.();
    };

    // Contain-fit: scale the design so it fits BOTH the height budget and the
    // available width. Previously desktop scaled by height only, so wide
    // designs (e.g. 3:1 email signatures) overflowed the card and the screen.
    const designWidth = Number(design.width);
    const designHeight = Number(design.height);
    const hasValidDimensions = designWidth > 0 && designHeight > 0;

    const maxWidth = isMobile
        ? window.innerWidth - 2 * mobilePadding
        : availableWidth;
    const maxHeight = isMobile
        ? Math.max(0, window.innerHeight - 2 * mobilePadding)
        : containerHeight;

    let displayScale = 1;
    if (hasValidDimensions) {
        displayScale = maxWidth > 0
            ? Math.min(maxWidth / designWidth, maxHeight / designHeight)
            // Not measured yet (first frame / hidden wrapper): height-only fit.
            : maxHeight / designHeight;
    }
    const displayHeight = hasValidDimensions ? designHeight * displayScale : containerHeight;
    const displayWidth = hasValidDimensions ? `${designWidth * displayScale}px` : '0px';

    const setRefs = (node: HTMLDivElement) => {
        (divRef as React.MutableRefObject<HTMLDivElement>).current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    return (
        <TemplateWrapper
            ref={wrapperRef}
            // Tapping the area around the board (not the board itself) deselects the
            // scene canvas — a reliable mobile deselect when full-bleed editable
            // layers cover the whole board (so a bg tap never hits a non-editable node).
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
                if (editable && design?.scene && e.target === e.currentTarget) endSceneSelection();
            }}
        >
            <TemplateContainer
                id="template-html-container"
                style={{
                    height: `${displayHeight}px`,
                    width: displayWidth
                }}
            >
                {design && design.scene ? (
                    // Canvas engine: react-konva renderer (scales itself; no DOM transform).
                    // Render whenever a scene payload exists (don't blank if `engine`
                    // metadata is missing). Canvas has no DOM mutations, so the parent's
                    // ready signal comes from SceneRenderer.onReady, not the observer.
                    <TemplateContent id="template-scene" ref={setRefs}>
                        <SceneRenderer
                            scene={mergedScene ?? design.scene}
                            values={values || {}}
                            fonts={fonts}
                            transforms={sceneTransforms}
                            imageFit={imageFit}
                            scale={hasValidDimensions ? displayScale : 1}
                            onReady={onTemplateLoad}
                            editable={editable}
                            selectedId={selectedSceneId}
                            // Reposition lock is active while an image is in the
                            // floating-bar transform stage (Reposition tapped).
                            repositionLock={!!selectedTransformedImage}
                            showTextHints={sceneTextHints}
                            onSelect={(id) => (id ? setSelectedSceneId(id) : endSceneSelection())}
                            onImageInputClick={onImageInputClick}
                            onTextInputClick={onTextInputClick}
                            onTransformChange={(id, t) =>
                                setSceneTransforms((prev) => ({...prev, [id]: {...prev[id], ...t}}))}
                            groupIds={groupIds}
                            onGroupMove={(dx, dy) => {
                                const rowKey = repositioningRow;
                                if (!rowKey || !design.scene) return;
                                setSceneTransforms((prev) => moveRow(prev, design.scene!, rowKey, dx, dy));
                            }}
                            onTextChange={(key, value) =>
                                setSelections((prev) => ({...prev, [key]: value}))}
                            textMeta={(key) => {
                                // A USER LAYER is not a template input, so the
                                // lookup below would return null and the inline
                                // editor would refuse to open on it.
                                if (isUserLayerId(key)) return {editable: true, multiline: true};
                                const input = template?.inputs?.[key];
                                if (!input || input.type !== 'text') return null;
                                return {
                                    editable: !!input.editable,
                                    maxChars: input.maxChars,
                                    multiline: input.inputType === 'textarea',
                                };
                            }}
                        />
                    </TemplateContent>
                ) : design && (
                    <TemplateContent
                        id="template-html"
                        ref={setRefs}
                        style={{
                            height: `${design.height}px`,
                            // Explicit width: a transformed absolutely-positioned element
                            // doesn't contribute scaled layout size, so don't rely on the
                            // HTML's intrinsic width.
                            width: `${design.width}px`,
                            transform: `scale(${hasValidDimensions ? displayScale : displayHeight / design.height})`,
                        }}
                        dangerouslySetInnerHTML={{__html: design.html || ''}}
                    />
                )}
            </TemplateContainer>
        </TemplateWrapper>
    );
});

export default TemplateDisplay;